import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchOfficeHours,
  fetchUpdateOfficeHours,
} from "../../../store/Practice/OfficeHours/OfficeHoursActions";
import { clearErrors } from "../../../store/Practice/OfficeHours/OfficeHoursReducer";

import {
  normalizeTimeRegexMatch,
  normalizeTime,
} from "../../../../src/utils/commonUtilities";

import CgCheckbox from "../../../common/CgCheckbox";
import PopupModal from "../../../common/PopupModal";
import "./officeHours.scss";
import { useLocation } from "react-router-dom";

const timeOptions = [
  "00:00:00.000",
  "00:30:00.000",
  "01:00:00.000",
  "01:30:00.000",
  "02:00:00.000",
  "02:30:00.000",
  "03:00:00.000",
  "03:30:00.000",
  "04:00:00.000",
  "04:30:00.000",
  "05:00:00.000",
  "05:30:00.000",
  "06:00:00.000",
  "06:30:00.000",
  "07:00:00.000",
  "07:30:00.000",
  "08:00:00.000",
  "08:30:00.000",
  "09:00:00.000",
  "09:30:00.000",
  "10:00:00.000",
  "10:30:00.000",
  "11:00:00.000",
  "11:30:00.000",
  "12:00:00.000",
  "12:30:00.000",
  "13:00:00.000",
  "13:30:00.000",
  "14:00:00.000",
  "14:30:00.000",
  "15:00:00.000",
  "15:30:00.000",
  "16:00:00.000",
  "16:30:00.000",
  "17:00:00.000",
  "17:30:00.000",
  "18:00:00.000",
  "18:30:00.000",
  "19:00:00.000",
  "19:30:00.000",
  "20:00:00.000",
  "20:30:00.000",
  "21:00:00.000",
  "21:30:00.000",
  "22:00:00.000",
  "22:30:00.000",
  "23:00:00.000",
  "23:30:00.000",
];

const OfficeHours = ({
  onlyPopup = false,
  openOfficeHoursPopup,
  closePopupCallback = () => {},
}) => {
  const location = useLocation();
  const locationData = location.pathname.split("/");
  const PRACTICE_ID = locationData[2];
  const [openPopup, setOpenPopup] = useState(false);
  const [formData, setFormData] = useState({});
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const days = useSelector(
    (state) => state.officeHours.data?.data?.officeHoursResponseDTO || {}
  );
  const isOfficeHoursLoading = useSelector(
    (state) => state.officeHours.isOfficeHoursLoading
  );
  const officeHoursError = useSelector(
    (state) => state.officeHours.officeHoursError
  );
  const officeHoursErrorMsg = useSelector(
    (state) => state.officeHours.officeHoursErrorMsg
  );
  const isUpdateOfficeHoursLoading = useSelector(
    (state) => state.updateOfficeHours.isUpdateOfficeHoursLoading
  );
  const updateOfficeHoursError = useSelector(
    (state) => state.updateOfficeHours.updateOfficeHoursError
  );
  const updateOfficeHoursErrorMsg = useSelector(
    (state) => state.updateOfficeHours.updateOfficeHoursErrorMsg
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (openOfficeHoursPopup || openPopup) {
      updateOfficeHours();
    }
  }, [openOfficeHoursPopup, openPopup]);

  const initialFetchDone = React.useRef(false);

  useEffect(() => {
    if (!initialFetchDone.current) {
      dispatch(fetchOfficeHours(PRACTICE_ID));
      initialFetchDone.current = true;
    }

    if (openOfficeHoursPopup !== undefined) {
      setTimeout(() => {
        setOpenPopup(openOfficeHoursPopup);
      }, 500);
    }
  }, [openOfficeHoursPopup]);

  useEffect(() => {
    return () => {
      dispatch(clearErrors());
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest(".custom-time-dropdown")) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeDropdown]);

  useEffect(() => {
    if (updateOfficeHoursError) {
      setShowError(true);
      setErrorMsg(updateOfficeHoursErrorMsg || "Failed to update office hours");
      setOpenPopup(false);
      setTimeout(() => {
        setShowError(false);
      }, 2000);
    }
  }, [updateOfficeHoursError]);

  useEffect(() => {
    if (
      officeHoursError &&
      officeHoursErrorMsg !== "The requested information could not be found."
    ) {
      setShowError(true);
      setErrorMsg("No office hours data found");
    }
  }, [officeHoursError, officeHoursErrorMsg]);

  const updateOfficeHours = () => {
    const initialFormData = {};
    Object.keys(days).map((day) => {
      initialFormData[day] = {
        officeStatus: days[day] && days[day].officeOpen ? "OPEN" : "CLOSED",
        officeOpen:
          days[day] && days[day].officeOpen
            ? formatTimeWithLeadingZeros(days[day]?.officeOpen)
            : "",
        officeClose:
          days[day] && days[day].officeClose
            ? formatTimeWithLeadingZeros(days[day]?.officeClose)
            : "",
        limited: days[day]?.limited || false,
      };
    });
    setFormData(initialFormData);
  };

  const handleChange = (day, e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prevFormData) => ({
      ...prevFormData,
      [day]: {
        ...prevFormData[day],
        [name]: type === "checkbox" ? checked : value,
        ...(name === "officeStatus" && value === "OPEN"
          ? {
              officeOpen: formatTimeWithLeadingZeros("07:00:00.000"),
              officeClose: formatTimeWithLeadingZeros("17:00:00.000"),
            }
          : {}),
        [`error_${name}`]: !normalizeTimeRegexMatch(value),
      },
    }));
  };

  const handleSubmit = (e) => {
    try {
      if (e && e.preventDefault) {
        e.preventDefault();
        closePopupCallback();
      }

      const payload = { ...days };
      Object.keys(formData).forEach((day) => {
        try {
          if (!formData[day]) return;
          const { officeOpen, officeClose, limited, officeStatus } =
            formData[day];
          if (officeStatus === "CLOSED") {
            payload[day] = {};
          } else if (officeStatus === "OPEN" && officeOpen && officeClose) {
            const formattedOfficeOpen = convertTo24HoursFormat(officeOpen);
            const formattedOfficeClose = convertTo24HoursFormat(officeClose);

            const hasChanges =
              !days[day] ||
              formattedOfficeOpen !== days[day]?.officeOpen ||
              formattedOfficeClose !== days[day]?.officeClose ||
              Boolean(limited) !== days[day]?.limited;

            if (hasChanges) {
              payload[day] = {
                officeOpen: formattedOfficeOpen,
                officeClose: formattedOfficeClose,
                limited: Boolean(limited),
              };
            }
          }
        } catch (error) {
          setErrorMsg("No office hours data found");
          setShowError(true);
          return error;
        }
      });

      const hasChanges = Object.keys(payload).some((day) => {
        const originalDay = days[day] || {};
        const updatedDay = payload[day] || {};
        return JSON.stringify(originalDay) !== JSON.stringify(updatedDay);
      });

      if (hasChanges) {
        dispatch(
          fetchUpdateOfficeHours({ practiceId: PRACTICE_ID, data: payload })
        )
          .then((response) => {
            if (response?.payload?.metadata?.outcome?.status === 200) {
              dispatch(fetchOfficeHours(PRACTICE_ID));
              setOpenPopup(false);
              setShowError(false);
              setErrorMsg("");
              if (closePopupCallback) {
                closePopupCallback();
              }
            } else {
              setOpenPopup(false);
              setErrorMsg("No office hours data found");
              setShowError(true);
              setTimeout(() => {
                setShowError(false);
              }, 2000);
            }
          })
          .catch(() => {
            setOpenPopup(false);
            setErrorMsg("No office hours data found");
            setShowError(true);
            setTimeout(() => {
              setShowError(false);
            }, 2000);
          });
      } else {
        setOpenPopup(false);
        if (closePopupCallback) {
          closePopupCallback();
        }
      }
    } catch {
      setOpenPopup(false);
      setErrorMsg("No office hours data found");
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 2000);
    }
  };

  const formatTime = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatTimeWithLeadingZeros = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));

    let hours12 = date.getHours() % 12;
    hours12 = hours12 === 0 ? 12 : hours12;
    const ampm = date.getHours() >= 12 ? "PM" : "AM";

    return `${hours12.toString().padStart(2, "0")}:${date
      .getMinutes()
      .toString()
      .padStart(2, "0")} ${ampm}`;
  };

  const formattedTimeOptions = timeOptions.map((time) =>
    formatTimeWithLeadingZeros(time)
  );

  const convertTo24HoursFormat = (time) => {
    if (!time) return "";
    const [timePart, modifier] = time.split(" ");
    let [hours, minutes] = timePart.split(":");
    hours = parseInt(hours, 10);
    if (modifier === "PM" && hours !== 12) {
      hours += 12;
    }
    if (modifier === "AM" && hours === 12) {
      hours = 0;
    }
    const formattedTime = `${hours
      .toString()
      .padStart(2, "0")}:${minutes.padStart(2, "0")}:00`;
    return formattedTime;
  };

  // const convertToHtmlTimeFormat = (time) => {
  //   if (!time) return "";
  //   const [timePart, modifier] = time.split(" ");
  //   let [hours, minutes] = timePart.split(":");
  //   hours = parseInt(hours, 10);
  //   if (modifier === "PM" && hours !== 12) {
  //     hours += 12;
  //   }
  //   if (modifier === "AM" && hours === 12) {
  //     hours = 0;
  //   }
  //   return `${hours.toString().padStart(2, "0")}:${minutes.padStart(2, "0")}`;
  // };

  const toggleTimeDropdown = (e, fieldName, day) => {
    e.stopPropagation();
    const dropdownId = `${fieldName}-${day}`;
    setActiveDropdown(activeDropdown === dropdownId ? null : dropdownId);
  };

  const copyMondayTimes = () => {
    const mondayData = formData["monday"];
    if (mondayData) {
      const updatedFormData = { ...formData };
      ["tuesday", "wednesday", "thursday", "friday"].map((day) => {
        updatedFormData[day] = {
          ...mondayData,
          limited: mondayData.limited,
        };
      });
      setFormData(updatedFormData);
    }
  };

  const RenderOfficeHoursErrors = () => {
    if (!showError || !errorMsg) return null;
    return (
      <div className="alert alert-danger mt-4" role="alert">
        {errorMsg}
      </div>
    );
  };

  const RenderEmptyCards = () => {
    const weekDays = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];
    return (
      <div className="full-width-container display-flex col-md-12">
        <div className="office-section">
          <h3 className="section-header top-thirty overview">
            WEEKLY VIEW
            <a
              type="button"
              className="btn edit-button"
              onClick={() => {
                setOpenPopup(!openPopup);
              }}
            >
              Edit
            </a>
          </h3>
        </div>
        <div className="office-hours-content">
          {weekDays.map((day, index) => (
            <div key={index} className="cards weekly-card">
              <div className="cards-header">
                <h6>{day.slice(0, 3).toUpperCase()}</h6>
              </div>
              <div className="closed-card cards-body"><p>CLOSED</p></div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const RenderWeeklyView = () => {
    if (isOfficeHoursLoading) {
      return <p>Loading...</p>;
    }

    if (
      officeHoursError &&
      officeHoursErrorMsg === "The requested information could not be found."
    ) {
      return <RenderEmptyCards />;
    }

    if (officeHoursError) {
      return null;
    }

    if (!days || Object.keys(days).length === 0) {
      return <RenderEmptyCards />;
    }

    return (
      <div className="full-width-container display-flex col-md-12">
        <div className="office-section">
          <h3 className="section-header top-thirty overview">
            WEEKLY VIEW
            <a
              type="button"
              className="btn edit-button"
              onClick={() => {
                setOpenPopup(!openPopup);
              }}
            >
              Edit
            </a>
          </h3>
        </div>
        <div className="office-hours-content">
          {Object.keys(days).map((day, index) => (
            <div key={index} className="cards weekly-card">
              <div className="cards-header">
                <h6>{day.slice(0, 3).toUpperCase()}</h6>
              </div>
              <div
                className={
                  days[day]?.officeClose
                    ? `cards-body`
                    : `closed-card cards-body`
                }
              >
                {days[day] && days[day].officeOpen ? (
                  <>
                    <span>OPEN</span>
                    <p> {formatTime(days[day]?.officeOpen)}</p>
                    <span>CLOSE</span>
                    <p> {formatTime(days[day]?.officeClose)}</p>
                    {days[day].limited && (
                      <p className="limited">Limited Availability</p>
                    )}
                  </>
                ) : (
                  <p>CLOSED</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const RenderOfficeHoursForm = () => {
    if (isOfficeHoursLoading) return <div> Loading ... </div>;

    if (Object.keys(formData).length === 0) {
      const weekDays = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];
      const initialFormData = {};
      weekDays.forEach((day) => {
        initialFormData[day] = {
          officeStatus: "CLOSED",
          officeOpen: "",
          officeClose: "",
          limited: false,
        };
      });
      setFormData(initialFormData);
    }

    return (
      <form onSubmit={handleSubmit}>
        <table className="office-hours-table">
          <tbody>
            {Object.keys(formData).map((day, index) => (
              <tr key={index}>
                <td className="daysOfweek">{day.slice(0, 3).toUpperCase()}</td>
                <td className="office-hours-tableData">
                  <tr>
                    <div className="officeStatusTime">
                      <td className="officeHoursStatus">Office is</td>
                      <td>
                        <div className="custom-time-dropdown">
                          <button
                            className="form-select time-select office-status-button"
                            value={formData[day]?.officeStatus || ""}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setActiveDropdown("OPEN_CLOSE" + day);
                            }}
                            name="officeStatus"
                            required
                          >
                            {formData?.[day]?.officeStatus}
                          </button>
                          {activeDropdown === "OPEN_CLOSE" + day ? (
                            <ui className="time-dropdown-menu">
                              <div
                                className="time-option"
                                value="OPEN"
                                onClick={() => {
                                  handleChange(day, {
                                    target: {
                                      name: "officeStatus",
                                      value: "OPEN",
                                    },
                                  });
                                  setActiveDropdown(null);
                                }}
                              >
                                OPEN
                              </div>
                              <div
                                className="time-option"
                                value="CLOSED"
                                onClick={() => {
                                  handleChange(day, {
                                    target: {
                                      name: "officeStatus",
                                      value: "CLOSED",
                                    },
                                  });
                                  setActiveDropdown(null);
                                }}
                              >
                                CLOSED
                              </div>
                            </ui>
                          ) : null}
                        </div>
                      </td>

                      {formData[day] &&
                        formData[day].officeStatus !== "CLOSED" && (
                          <>
                            <td>
                              <label>From </label>
                            </td>
                            <td>
                              <div className="custom-time-dropdown">
                                <input
                                  type="text"
                                  className={`form-select time-select ${
                                    formData[day]?.[`error_officeOpen`]
                                      ? "error"
                                      : ""
                                  }`}
                                  name="officeOpen"
                                  value={formData[day]?.officeOpen || ""}
                                  onChange={(e) =>
                                    handleChange(day, {
                                      target: {
                                        name: "officeOpen",
                                        value: e.target.value,
                                      },
                                    })
                                  }
                                  onClick={(e) =>
                                    toggleTimeDropdown(e, "officeOpen", day)
                                  }
                                  placeholder="Select"
                                  required
                                  onBlur={() => {
                                    if (
                                      typeof formData[day]?.officeOpen ===
                                      "string"
                                    ) {
                                      handleChange(day, {
                                        target: {
                                          name: "officeOpen",
                                          value: normalizeTime(
                                            formData[day]?.officeOpen
                                          ),
                                        },
                                      });
                                    }
                                  }}
                                />
                                {activeDropdown === `officeOpen-${day}` && (
                                  <div className="time-dropdown-menu">
                                    {formattedTimeOptions.map((time, idx) => (
                                      <div
                                        key={idx}
                                        className="time-option"
                                        onClick={() => {
                                          handleChange(day, {
                                            target: {
                                              name: "officeOpen",
                                              value: time,
                                            },
                                          });
                                          setActiveDropdown(null);
                                        }}
                                      >
                                        {time}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <label>To </label>
                            </td>
                            <td className="timeslots">
                              <div className="custom-time-dropdown">
                                <input
                                  type="text"
                                  className={`form-select time-select ${
                                    formData[day]?.[`error_officeClose`]
                                      ? "error"
                                      : ""
                                  }`}
                                  name="officeClose"
                                  value={formData[day]?.officeClose || ""}
                                  onChange={(e) =>
                                    handleChange(day, {
                                      target: {
                                        name: "officeClose",
                                        value: e.target.value,
                                      },
                                    })
                                  }
                                  onClick={(e) =>
                                    toggleTimeDropdown(e, "officeClose", day)
                                  }
                                  placeholder="Select"
                                  required
                                  onBlur={() => {
                                    if (
                                      typeof formData[day]?.officeClose ===
                                      "string"
                                    ) {
                                      handleChange(day, {
                                        target: {
                                          name: "officeClose",
                                          value: normalizeTime(
                                            formData[day]?.officeClose
                                          ),
                                        },
                                      });
                                    }
                                  }}
                                />
                                {activeDropdown === `officeClose-${day}` && (
                                  <div className="time-dropdown-menu">
                                    {formattedTimeOptions.map((time, idx) => (
                                      <div
                                        key={idx}
                                        className="time-option"
                                        onClick={() => {
                                          handleChange(day, {
                                            target: {
                                              name: "officeClose",
                                              value: time,
                                            },
                                          });
                                          setActiveDropdown(null);
                                        }}
                                      >
                                        {time}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <CgCheckbox
                                options={[
                                  {
                                    label: (
                                      <span className="limited-text">
                                        Limited Availability
                                        <span className="alternate-weeks-text">
                                          (Alternate weeks or similar)
                                        </span>
                                      </span>
                                    ),
                                    value: true,
                                  },
                                ]}
                                onSelect={(values) =>
                                  handleChange(day, {
                                    target: {
                                      name: "limited",
                                      value: values?.includes(true),
                                    },
                                  })
                                }
                                defaultValues={
                                  formData[day]?.limited ? [true] : []
                                }
                                name="limited"
                              />
                            </td>
                          </>
                        )}
                    </div>
                  </tr>
                  <tr>
                    <td>
                      {day === "monday" && (
                        <div className="d-flex align-items-center copy-wrap">
                          <button
                            type="button"
                            className="saveBtn"
                            onClick={copyMondayTimes}
                          >
                            Copy
                          </button>
                          <span className="officeSpan">
                            Click COPY if you want to keep the hours for Tue-Fri
                            the same as Mon.
                          </span>
                        </div>
                      )}
                    </td>
                  </tr>
                </td>
                <td></td>
              </tr>
            ))}
          </tbody>
        </table>
      </form>
    );
  };

  const RenderEditModal = () => {
    return (
      <PopupModal
        customRender={RenderOfficeHoursForm}
        isOpen={openPopup}
        onClose={() => {
          setOpenPopup(false);
          setFormData({});
          dispatch(clearErrors());
          if (closePopupCallback) {
            closePopupCallback();
          }
        }}
        onSave={handleSubmit}
        popupHeading="OFFICE HOURS"
        enableSave={!isUpdateOfficeHoursLoading}
      />
    );
  };

  return (
    <div className="office-hours-wrapper">
      {RenderOfficeHoursErrors()}
      {!onlyPopup && RenderWeeklyView()}
      {openPopup && RenderEditModal()}
    </div>
  );
};

export default OfficeHours;
